/**
 * AgentActivityChip
 *
 * Shows, at a glance, whether an agent is currently editing one of the
 * user's tabs and for how long — "Agent editing ‹tab name› · ‹elapsed›"
 * (Task T17, AI Smoothness Wave, F2). Reads the same per-tab AI lock that
 * the tab strip (`Tabs.tsx`, `tab-ai-locked-${tabId}`) and the Query Editor
 * status bar (`QueryStatusBar.tsx`, `status-bar-ai-editing`) already surface
 * — this is the AI terminal context bar's version of the same signal, so an
 * agent's activity is visible without having to go find the locked tab.
 *
 * Extracted into its own file per the same ≤200-line-file precedent T15
 * (`AddSkillNudgeRow.tsx`) and T16 (`PersistentWorkspaceToggleRow.tsx`)
 * followed — `AiTerminalContextBar.tsx` only imports and mounts it.
 *
 * Display-only, no analytics: the CLAUDE.md tracking mandate covers
 * user-actionable surfaces. A passive, once-a-second ticking status readout
 * is not one, and firing an event every tick would be a telemetry flood.
 *
 * `AiLockInfo` carries no agent identity (`sessionId`, `tabName`, a
 * free-text `message` — never a name), so the visible label always says the
 * generic "Agent" rather than guessing one from `message`.
 */

import { useMemo } from 'react';
import { Bot } from 'lucide-react';
import { useAiLockStore } from '../store/aiLockStore';
import { useAppStore } from '../../../store/appStore';
import { useWorkspaceStore } from '../../workspace/core/store/workspaceStore';
import { useLockDuration } from '../hooks/useLockDuration';
import { displayText } from '../hooks/executionRequestPrompt';

type AgentActivityChipProps = {
  /** The AI terminal session this chip belongs to — only its own lock is shown. */
  sessionId: string;
};

/**
 * Characters of the agent-supplied lock `message` shown in the chip's
 * tooltip. `message` is free text written straight from a `.meta` lock file
 * by an external agent process — never sanitized upstream — so it is capped
 * and whitespace-collapsed here via the shared `displayText` helper (same
 * one `executionRequestPrompt.ts` uses for the execute-confirm prompt's
 * `reason` text) before it ever reaches the DOM.
 */
const MAX_LOCK_MESSAGE_DISPLAY_LENGTH = 60;

/**
 * Characters of the tab name shown inline. Tab titles are app-owned (not
 * directly agent free text), but a user or agent can still rename a tab to
 * something very long, so this is capped defensively for the same "must not
 * reflow or blow out the bar" reason.
 */
const MAX_TAB_NAME_DISPLAY_LENGTH = 60;

export function AgentActivityChip({ sessionId }: AgentActivityChipProps) {
  const lockedTabs = useAiLockStore((s) => s.lockedTabs);
  const getTab = useWorkspaceStore((s) => s.getTab);
  const lockTimeoutMinutes = useAppStore((s) => s.aiTerminalSettings.aiWorkspace.lockTimeoutMinutes);

  // Only THIS session's own lock — a lock belonging to a different AI
  // terminal session (or the MCP tool's session-less `lock_tab` lock) must
  // never show here (ruling F0.5). Map iteration is insertion order, so if
  // more than one tab is somehow locked for the same session at once, the
  // oldest still-active lock wins — a stable, deterministic pick.
  const lockInfo = useMemo(() => {
    for (const info of lockedTabs.values()) {
      if (info.sessionId === sessionId) return info;
    }
    return undefined;
  }, [lockedTabs, sessionId]);

  // Always called, unconditionally, before any early return (rules of
  // hooks). Internally a no-op — no `setInterval` at all — when `lockInfo`
  // is undefined, and its cleanup fires `clearInterval` the instant
  // `lockInfo` transitions away from a lock, so the 1s tick can never
  // outlive the lock. `getDurationSince`/`formatLockDuration` (used inside)
  // already make the counter unfalsifiable: an unparseable or future
  // `lockedAt` both clamp to 0 ("0s", never `NaN`/negative/Invalid Date),
  // and anything past an hour renders as "‹h›h ‹m›m" instead of a raw
  // multi-thousand-second count.
  //
  // The hook also returns `isStale`/`durationMs`, both unused here on
  // purpose: staleness styling is `Tabs.tsx`'s job (its tab-strip indicator
  // already turns purple and its tooltip already says "stale") and
  // `StaleLockMonitor.tsx` already owns the stale toast — this chip stays a
  // single calm "an agent is active" signal rather than duplicating that.
  const duration = useLockDuration(lockInfo, lockTimeoutMinutes);

  if (!lockInfo) return null;

  const tab = getTab(lockInfo.tabId);
  const tabName = displayText(tab?.title ?? lockInfo.tabName ?? 'a tab', MAX_TAB_NAME_DISPLAY_LENGTH) || 'a tab';
  const message = lockInfo.message ? displayText(lockInfo.message, MAX_LOCK_MESSAGE_DISPLAY_LENGTH) : '';
  // `duration` is only ever null when `lockInfo` is (useLockDuration.ts:39),
  // so this is always populated once we're past the guard above — the `??`
  // exists solely so TypeScript doesn't need a second, always-true-here
  // null check on a separately-typed variable.
  const elapsed = duration?.formattedDuration ?? '0s';

  return (
    <div
      data-testid="ai-terminal-activity-chip"
      className="flex items-center gap-1 flex-shrink-0 text-purple-500"
      title={message ? `Agent editing "${tabName}": ${message}` : `Agent editing "${tabName}"`}
    >
      <Bot className="h-3 w-3 flex-shrink-0 animate-pulse" />
      {/* `max-w-` + `truncate` directly on the span, matching
          `ConnectionSelector.tsx`'s `(${serverHost})` span — explicit and
          self-contained rather than relying on the ancestor's `max-width`
          plus `.truncate`'s own `overflow: hidden` (which already exempts a
          flex item from the "min-width: auto = full content width" gotcha
          per the CSS Flexbox spec's automatic-minimum-size rule — verified
          empirically in fix round 1: `scrollWidth` 439px vs `offsetWidth`
          204px on the un-annotated span, i.e. ellipsis was already clipping
          correctly). Kept here anyway as the more explicit, self-contained,
          convention-matching form: it doesn't depend on a child inheriting
          the right behavior from a sibling's CSS property. */}
      <span data-testid="ai-terminal-activity-chip-label" className="truncate max-w-[220px]">
        Agent editing {tabName} · {elapsed}
      </span>
    </div>
  );
}
